import type { LimitLink } from "../../../../utils/limitErrorHandler";
import { useContext, useCallback } from "react";
import { uploadContext } from "../../../../stores/upload/uploadContext";

export interface UseFileUploadReturn {
  file: File | null;
  projectTitle: string;
  uploadProgress: number;
  uploadEtaSeconds: number | null;
  uploadFinishing: boolean;
  isUploading: boolean;
  error: string | null;
  transcriptionEngine: string;
  audioTrack: number;
  languages: string[];
  batchCurrent: number;
  batchTotal: number;
  handleFileSelect: (file: File | null) => void;
  handleFileDrop: (files: FileList | null) => void;
  setProjectTitle: (title: string) => void;
  setTranscriptionEngine: (engine: string) => void;
  setAudioTrack: (track: number) => void;
  setLanguages: (langs: string[]) => void;
  uploadFile: (title?: string) => Promise<boolean>;
  uploadBatch: (files: File[], onAllDone?: () => void) => Promise<void>;
  resetUpload: () => void;
  validateFile: (file: File) => { isValid: boolean; error?: string };
}

export function useFileUpload(
  onSuccess?: (message: string) => void,
  onError?: (error: string, limitLink?: LimitLink) => void,
): UseFileUploadReturn {
  const store = useContext(uploadContext);

  const validateFile = useCallback(
    (fileToValidate: File): { isValid: boolean; error?: string } => {
      return store.validateFile(fileToValidate);
    },
    [store],
  );

  const handleFileSelect = useCallback(
    (selectedFile: File | null) => {
      if (!selectedFile) {
        store.setFile(null);
        return;
      }
      const validation = store.validateFile(selectedFile);
      if (!validation.isValid) {
        store.setFile(null);
        onError?.(validation.error || "Invalid file");
        return;
      }
      store.setFile(selectedFile);
    },
    [store, onError],
  );

  const handleFileDrop = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      handleFileSelect(files[0]);
    },
    [handleFileSelect],
  );

  const uploadFile = useCallback(
    async (title?: string): Promise<boolean> => {
      let succeeded = false;
      await store.uploadFile(
        title || "",
        (msg) => {
          succeeded = true;
          onSuccess?.(msg);
        },
        onError,
      );
      return succeeded;
    },
    [store, onSuccess, onError],
  );

  const uploadBatch = useCallback(
    async (files: File[], onAllDone?: () => void) => {
      await store.uploadBatch(files, store.transcriptionEngine, onSuccess, onError, onAllDone);
    },
    [store, onSuccess, onError],
  );

  const resetUpload = useCallback(() => {
    store.reset();
  }, [store]);

  return {
    file: store.file,
    projectTitle: store.projectTitle,
    uploadProgress: store.uploadProgress,
    uploadEtaSeconds: store.uploadEtaSeconds,
    uploadFinishing: store.uploadFinishing,
    isUploading: store.isUploading,
    error: store.error,
    transcriptionEngine: store.transcriptionEngine,
    audioTrack: store.audioTrack,
    languages: store.languages,
    batchCurrent: store.batchCurrent,
    batchTotal: store.batchTotal,
    handleFileSelect,
    handleFileDrop,
    setProjectTitle: store.setProjectTitle,
    setTranscriptionEngine: store.setTranscriptionEngine,
    setAudioTrack: store.setAudioTrack,
    setLanguages: store.setLanguages,
    uploadFile,
    uploadBatch,
    resetUpload,
    validateFile,
  };
}
